import { Host, h } from "@stencil/core";
import { CHECKBOX_PARTS_DICTIONARY, DISABLED_CLASS } from "../../common/reserved-names";
import { updateDirectionInImageCustomVar } from "../../common/utils";
import { DEFAULT_GET_IMAGE_PATH_CALLBACK, getControlRegisterProperty } from "../../common/registry-properties";
import { getElementInternalsLabel } from "../../common/analysis/accessibility";
const PARTS = (checked, indeterminate, disabled) => {
    if (indeterminate) {
        return disabled
            ? `${CHECKBOX_PARTS_DICTIONARY.DISABLED} ${CHECKBOX_PARTS_DICTIONARY.INDETERMINATE}`
            : CHECKBOX_PARTS_DICTIONARY.INDETERMINATE;
    }
    const checkedValue = checked
        ? CHECKBOX_PARTS_DICTIONARY.CHECKED
        : CHECKBOX_PARTS_DICTIONARY.UNCHECKED;
    return disabled
        ? `${CHECKBOX_PARTS_DICTIONARY.DISABLED} ${checkedValue}`
        : checkedValue;
};
/**
 * @status developer-preview
 *
 * @part container - The container that serves as a wrapper for the `input` and the `option` parts.
 * @part input - The input element that implements the interactions for the component.
 * @part option - The actual "input" that is rendered above the `input` part. This part has `position: absolute` and `pointer-events: none`.
 * @part label - The label that is rendered when the `caption` property is not empty.
 *
 * @part checked - Present in the `input`, `option`, `label` and `container` parts when the control is checked and not indeterminate (`value` === `checkedValue` and `indeterminate !== true`).
 * @part disabled - Present in the `input`, `option`, `label` and `container` parts when the control is disabled (`disabled` === `true`).
 * @part indeterminate - Present in the `input`, `option`, `label` and `container` parts when the control is indeterminate (`indeterminate` === `true`).
 * @part unchecked - Present in the `input`, `option`, `label` and `container` parts when the control is unchecked and not indeterminate (`value` === `unCheckedValue` and `indeterminate !== true`).
 */
export class ChCheckBox {
    constructor() {
        this.accessibleName = undefined;
        this.caption = undefined;
        this.checkedValue = undefined;
        this.disabled = false;
        this.getImagePathCallback = undefined;
        this.highlightable = false;
        this.indeterminate = false;
        this.name = undefined;
        this.readonly = false;
        this.startImgSrc = undefined;
        this.startImgType = "background";
        this.unCheckedValue = undefined;
        this.value = undefined;
    }
    #accessibleNameFromExternalLabel;
    #startImage;
    // Refs
    #inputRef;
    internals;
    el;
    getImagePathCallbackChanged() {
        this.#startImage = this.#computeImage();
    }
    startImgSrcChanged() {
        this.#startImage = this.#computeImage();
    }
    valueChanged(newValue) {
        // Update form value
        this.internals.setFormValue(newValue?.toString());
    }
    /**
     * Emitted when the element is clicked or the space key is pressed and
     * released.
     */
    click;
    /**
     * The `input` event is emitted when a change to the element's value is
     * committed by the user.
     *
     * It contains the new value of the control.
     */
    input;
    #computeImage = () => {
        if (!this.startImgSrc) {
            return null;
        }
        const getImagePathCallback = this.getImagePathCallback ??
            getControlRegisterProperty("getImagePathCallback", "ch-checkbox") ??
            DEFAULT_GET_IMAGE_PATH_CALLBACK;
        if (!getImagePathCallback) {
            return null;
        }
        const img = getImagePathCallback(this.startImgSrc);
        return img
            ? updateDirectionInImageCustomVar(img, "start")
            : null;
    };
    #handleChange = (event) => {
        event.stopPropagation();
        this.#updateCheckedValueAndEmitEvent(this.#getInputRef().checked);
        // TODO: What's the need for this implementation in GeneXus
        if (this.highlightable) {
            this.click.emit();
        }
    };
    #handleHostClick = (event) => {
        const clickWasPerformedInAExternalLabel = event.detail === 0;
        if (!clickWasPerformedInAExternalLabel) {
            event.stopPropagation();
            return;
        }
        // When the internal label is clicked it provokes a click in the input with
        // event.detail === 0
        if (event.composedPath()[0] !== this.#getInputRef()) {
            return this.#updateCheckedValueAndEmitEvent(!this.#getInputRef().checked);
        }
        const rootNode = this.el.getRootNode();
        // TODO: This is a WA to make work again the ch-checkbox inside the
        // ch-tree-view-item, because the Space key is not working
        if (rootNode instanceof ShadowRoot &&
            rootNode.host.tagName.toLowerCase() ===
                "ch-tree-view-item") {
            this.#updateCheckedValueAndEmitEvent(this.#getInputRef().checked);
        }
    };
    #updateCheckedValueAndEmitEvent = (checked) => {
        const value = checked ? this.checkedValue : this.unCheckedValue;
        this.value = value;
        this.#getInputRef().value = value; // Update input's value before emitting the event
        // When the checked value is updated by the user, the control must no
        // longer be indeterminate
        this.indeterminate = false;
        this.input.emit(value);
    };
    // TODO: There is a bug in StencilJS where we can't use the same variable for
    // conditional rendered refs
    #getInputRef = () => this.#inputRef ?? this.el.shadowRoot.querySelector("input");
    #renderOption = (canAddListeners, checked, additionalParts) => {
        return (h("div", { class: "container", part: `${CHECKBOX_PARTS_DICTIONARY.CONTAINER} ${additionalParts}` }, h("input", { "aria-label": this.#accessibleNameFromExternalLabel ?? this.accessibleName, class: "input", part: `${CHECKBOX_PARTS_DICTIONARY.INPUT} ${additionalParts}`, type: "checkbox", checked: checked, disabled: this.disabled || this.readonly, indeterminate: this.indeterminate, value: this.value, onInput: canAddListeners && this.#handleChange, ref: el => (this.#inputRef = el) }), h("div", { class: {
                option: true,
                "option--not-displayed": !checked && !this.indeterminate,
                "option--checked": checked && !this.indeterminate,
                "option--indeterminate": this.indeterminate
            }, part: `${CHECKBOX_PARTS_DICTIONARY.OPTION} ${additionalParts}`, "aria-hidden": "true" })));
    };
    connectedCallback() {
        this.#startImage = this.#computeImage();
        // Set initial value to unchecked if empty
        this.value ||= this.unCheckedValue;
        // Accessibility
        this.internals.setFormValue(this.value?.toString());
        const labels = this.internals.labels;
        this.#accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue. TODO: It should take into account the caption property
        // analyzeLabelExistence(
        //   this.el,
        //   "ch-checkbox",
        //   labels,
        //   this.#accessibleNameFromExternalLabel,
        //   this.accessibleName
        // );
    }
    render() {
        const checked = this.value === this.checkedValue;
        const additionalParts = PARTS(checked, this.indeterminate, this.disabled);
        const startImageClasses = this.#startImage?.classes;
        const canAddListeners = !this.disabled && !this.readonly;
        const hasStartImageStyles = !!this.#startImage?.styles;
        return (h(Host, { key: '20ec56329251ccf40cd1805acacd7ef9c3982eda', class: {
                [DISABLED_CLASS]: this.disabled,
                "ch-checkbox--actionable": (!this.readonly && !this.disabled) ||
                    (this.readonly && this.highlightable)
            }, onClickCapture: canAddListeners && this.#handleHostClick }, this.caption || hasStartImageStyles ? (h("label", { class: {
                label: true,
                [startImageClasses]: !!startImageClasses,
                [`start-img-type--${this.startImgType} pseudo-img--start`]: hasStartImageStyles
            }, part: `${CHECKBOX_PARTS_DICTIONARY.LABEL} ${additionalParts}`, style: hasStartImageStyles ? this.#startImage.styles : undefined }, this.#renderOption(canAddListeners, checked, additionalParts), this.caption)) : (this.#renderOption(canAddListeners, checked, additionalParts))));
    }
    static get is() { return "ch-checkbox"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["checkbox.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["checkbox.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the label of the checkbox."
                },
                "attribute": "caption",
                "reflect": false
            },
            "checkedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value when the checkbox is 'on'"
                },
                "attribute": "checked-value",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    imageSrc: string\n  ) => GxImageMultiState | undefined",
                    "resolved": "(imageSrc: string) => GxImageMultiState",
                    "references": {
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc needs to be resolved."
                }
            },
            "highlightable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "True to highlight control when an action is fired."
                },
                "attribute": "highlightable",
                "reflect": false,
                "defaultValue": "false"
            },
            "indeterminate": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control's value is indeterminate."
                },
                "attribute": "indeterminate",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies the `name` of the control when used in a form."
                },
                "attribute": "name",
                "reflect": true
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that the user cannot modify the value of the control.\nSame as [readonly](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly)\nattribute for `input` elements."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "startImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the source of the start image."
                },
                "attribute": "start-img-src",
                "reflect": false
            },
            "startImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<ImageRender, \"img\">",
                    "resolved": "\"background\" | \"mask\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ImageRender": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the source of the start image."
                },
                "attribute": "start-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "unCheckedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The value when the switch is 'off'. If you want to not add the value when\nthe control is used in a form and it's unchecked, just let this property\nwith the default `undefined` value."
                },
                "attribute": "un-checked-value",
                "reflect": false
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The value of the control."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get events() {
        return [{
                "method": "click",
                "name": "click",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the element is clicked or the space key is pressed and\nreleased."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `input` event is emitted when a change to the element's value is\ncommitted by the user.\n\nIt contains the new value of the control."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }, {
                "propName": "startImgSrc",
                "methodName": "startImgSrcChanged"
            }, {
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
